import React, {
  useState,
  useEffect,
  FC,
  useCallback,
  useRef,
  useMemo,
} from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { OpenIcon } from '@adsk/alloy-react-icon';
import { MenuList, MenuItem } from '@adsk/alloy-react-menu';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';

import AboutBoxModal from './AboutBoxModal';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import loadSupportScripts from '../helpers/loadSupportScripts';
import { Collection, LicenseDataType } from '../helpers/types';
import { useTranslation } from '../i18n';
import HelpIcon from '../svg/helpIcon';

declare global {
  interface Window {
    zoomCampaignSdk: {
      show: () => void;
      hide: () => void;
      close: () => void;
      open: () => void;
    };
    zoomCampaignSdkConfig: {
      enableSPAMode: boolean;
    };
  }
}

const defaultHelpLink = 'https://help.autodesk.com/view/BUILD/ENU/';

type HelpLinksType = {
  help?: string;
  about?: string;
};

type QuestionMarkMenuProps = {
  initialOpen?: boolean;
  helpLinks?: HelpLinksType;
  currentProductId?: string;
  licenseData?: LicenseDataType | Collection[];
  isProductRequired?: boolean;
  hideContactSupport?: boolean;
};

const QuestionMarkMenu: FC<QuestionMarkMenuProps> = ({
  initialOpen,
  helpLinks,
  licenseData,
  currentProductId,
  isProductRequired,
  hideContactSupport,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(!!initialOpen);
  const [aboutBoxOpen, setAboutBoxOpen] = useState(false);
  const [zvaLoaded, setZvaLoaded] = useState(window.SUPPORT_SCRIPTS_LOADED);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const toggleAboutBox = () => {
    setAboutBoxOpen(true);
    setMenuOpen(false);
    analytics.track(
      ANALYTICS_TYPES.TOPNAVIGATION_HELP_LICENSEATTRIBUTIONS_CLICK,
    );
  };

  const toggleSupport = () => {
    if (window?.zoomCampaignSdk) {
      window.zoomCampaignSdk.show();
      window.zoomCampaignSdk.open();
    }

    setMenuOpen(false);
    analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CONTACTSUPPORT_CLICK);
  };

  useEffect(() => {
    if (currentProductId || !isProductRequired) {
      loadSupportScripts(() => {
        if (mountedRef.current) {
          setZvaLoaded(true);
        }
      }, currentProductId);
    }
  }, [currentProductId, isProductRequired]);

  const closeChatWindow = useCallback(() => {
    setZvaLoaded(true);
    window.zoomCampaignSdk?.close();
    window.zoomCampaignSdk?.hide();
  }, []);

  useEffect(() => {
    window.addEventListener('zoomCampaignSdk:ready', closeChatWindow);

    return () => {
      window.removeEventListener('zoomCampaignSdk:ready', closeChatWindow);
    };
  }, [closeChatWindow]);

  const helpLink = useMemo(
    () => helpLinks?.help || defaultHelpLink,
    [helpLinks?.help],
  );

  return (
    <>
      <AboutBoxModal
        show={aboutBoxOpen}
        onClose={() => setAboutBoxOpen(false)}
        licenseData={licenseData}
      />
      <Flyout
        offset={20}
        show={menuOpen}
        onHide={() => setMenuOpen(false)}
        onShow={() => {
          setMenuOpen(true);
        }}
        placement={Flyout.PLACEMENTS.BOTTOM}
        style={{ padding: 0 }}
        content={
          <MenuList style={{ width: 300 }}>
            <MenuItem
              as="a"
              href={helpLink}
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CLICK, {
                  source_page: window.location.href,
                  target_url: helpLink,
                });
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{t('HELP')}</span>
              <OpenIcon size={16} style={{ marginLeft: 16 }} />
            </MenuItem>
            <MenuItem
              as="a"
              href="https://learnacc.autodesk.com/"
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(
                  ANALYTICS_TYPES.TOPNAVIGATION_HELP_LEARNINGCENTER_CLICK,
                );
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{t('LEARNING_CENTER')}</span>
              <OpenIcon size={16} style={{ marginLeft: 16 }} />
            </MenuItem>
            {!hideContactSupport && (
              <MenuItem
                nested={false}
                disabled={!zvaLoaded}
                showSelection={false}
                onClick={toggleSupport}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{t('CONTACT_SUPPORT')}</span>
                {!zvaLoaded && (
                  <ProgressRing size={ProgressRing.SIZES.XSMALL} />
                )}
              </MenuItem>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <MenuItem
              as="a"
              href="https://www.autodesk.com/company/terms-of-use/en/general-terms"
              target="_blank"
              rel="noreferrer noopener"
              nested={false}
              showSelection={false}
              onClick={() => {
                setMenuOpen(false);
                analytics.track(
                  ANALYTICS_TYPES.TOPNAVIGATION_HELP_TERMSCONDITIONS_CLICK,
                );
              }}
              style={{
                justifyContent: 'space-between',
                padding: '10px 16px',
              }}
            >
              <span>{t('TERMS_AND_CONDITIONS')}</span>
              <OpenIcon size={16} />
            </MenuItem>
            {!helpLinks?.about && (
              <MenuItem
                nested={false}
                showSelection={false}
                onClick={toggleAboutBox}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{t('LICENSE_ATTRIBUTIONS')}</span>
              </MenuItem>
            )}
            {helpLinks?.about && (
              <MenuItem
                as="a"
                href={helpLinks?.about}
                target="_blank"
                rel="noreferrer noopener"
                nested={false}
                showSelection={false}
                onClick={() => setMenuOpen(false)}
                style={{
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                }}
              >
                <span>{t('ABOUT')}</span>
                <OpenIcon size={16} style={{ marginLeft: 16 }} />
              </MenuItem>
            )}
          </MenuList>
        }
      >
        <BasicButton
          onClick={() => setMenuOpen(!menuOpen)}
          data-testid="TopNavigation__help"
          style={{
            color: theme.colors.charcoal500,
            paddingRight: '0',
            '&:hover': {
              color: theme.colors.charcoal700,
            },
          }}
        >
          <HelpIcon />
        </BasicButton>
      </Flyout>
    </>
  );
};

QuestionMarkMenu.displayName = 'QuestionMarkMenu';

export default WithAlloySuspense(QuestionMarkMenu);
